'use client';

import { c as _c } from "react/compiler-runtime";
import { Accordion } from '@common/components/molecule/Accordion';
import SvgWelloPoint from '@common/icon/SvgWelloPoint';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import Image from 'next/image';
import { useSyncExternalStore } from 'react';
import { AuthQueryOptions, UnauthQueryOptions } from '@/api';
import { calculateInviteReward } from '@/app/donation/_utils/inviteReward';
import { formatRewardTotal, parseRewardAmount } from '@/app/donation/_utils/rewardProductName';
import { COOKIES_KEYS } from '@/constants';
import { WEBVIEW_TYPE_ENTRY } from '@/constants/entry';
import { useIsOutsideWelloChannel } from '@/hooks/useIsOutsideWelloChannel';
import { useWebviewTypeEntry } from '@/hooks/useWebviewTypeEntry';
import { useAuthStore } from '@/stores/AuthStore';
import { cookies } from '@/utils/cookies';
import WandImage from './event-reward-wand.png';
const ACCORDION_VALUE = 'event-reward';
interface EventRewardCardProps {
  productNumber: string;
  donationAmount: number;
  defaultExpanded?: boolean;
  mobileTitle?: string;
  className?: string;
}
export const eventRewardQueryOptions = ({
  productNumber,
  entry,
  isLogin
}: {
  productNumber: string;
  entry: ReturnType<typeof useWebviewTypeEntry>['entry'];
  isLogin: boolean;
}) => {
  const queryOptions = isLogin ? AuthQueryOptions['get:/wello/v3/auth/home-donation-event/reward'] : UnauthQueryOptions['get:/wello/v3/allows/home-donation-event/reward'];
  return queryOptions({
    query: {
      productNumber,
      entry
    }
  });
};
export const EventRewardCard = props => {
  const $ = _c(4);
  const {
    entry,
    isEntryResolved
  } = useWebviewTypeEntry();
  const isLogin = useAuthStore(_temp);
  if (!isEntryResolved || isLogin === undefined) {
    return null;
  }
  let t0;
  if ($[0] !== entry || $[1] !== isLogin || $[2] !== props) {
    t0 = <EventRewardCardContent {...props} entry={entry} isLogin={isLogin} />;
    $[0] = entry;
    $[1] = isLogin;
    $[2] = props;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
const EventRewardCardContent = t0 => {
  const $ = _c(41);
  const {
    productNumber,
    donationAmount,
    defaultExpanded: t1,
    mobileTitle,
    className,
    entry,
    isLogin
  } = t0;
  const defaultExpanded = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== entry || $[1] !== isLogin || $[2] !== productNumber) {
    t2 = eventRewardQueryOptions({
      productNumber,
      entry,
      isLogin
    });
    $[0] = entry;
    $[1] = isLogin;
    $[2] = productNumber;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = {
      ...t2,
      select: _temp2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data
  } = useSuspenseQuery(t3);
  const isOutsideWelloChannel = useIsOutsideWelloChannel();
  const myMemberCode = useAuthStore(_temp3);
  const inviteMemberCode = useSyncExternalStore(_temp4, _temp5, _temp6);
  const isInviteVisible = !!inviteMemberCode && inviteMemberCode !== myMemberCode && !isOutsideWelloChannel;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = UnauthQueryOptions["get:/wello/v2/allows/partners/reward-prices"]();
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isInviteVisible) {
    t5 = {
      ...t4,
      select: _temp7,
      enabled: isInviteVisible
    };
    $[7] = isInviteVisible;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data: inviteUnitAmount
  } = useQuery(t5);
  let t6;
  if ($[9] !== donationAmount || $[10] !== inviteUnitAmount || $[11] !== isInviteVisible) {
    t6 = isInviteVisible ? calculateInviteReward({
      donationAmount,
      unitAmount: inviteUnitAmount
    }) : 0;
    $[9] = donationAmount;
    $[10] = inviteUnitAmount;
    $[11] = isInviteVisible;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const inviteAmount = t6;
  let rewards;
  let t7;
  let t8;
  if ($[13] !== data?.contents || $[14] !== inviteAmount) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb0: {
      rewards = [...(data?.contents ?? []), ...(inviteAmount > 0 ? [{
        eventName: "\uCE5C\uAD6C\uCD08\uB300 \uC774\uBCA4\uD2B8",
        rewardProductName: String(inviteAmount)
      }] : [])];
      if (rewards.length === 0) {
        t8 = null;
        break bb0;
      }
      t7 = formatRewardTotal(rewards.map(_temp8));
    }
    $[13] = data?.contents;
    $[14] = inviteAmount;
    $[15] = rewards;
    $[16] = t7;
    $[17] = t8;
  } else {
    rewards = $[15];
    t7 = $[16];
    t8 = $[17];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  const totalRewardText = t7;
  let t9;
  if ($[18] !== mobileTitle) {
    t9 = mobileTitle && <h2 className="hidden font-700 text-Gray-900 typo-body2 mobile-tablet:block">{mobileTitle}</h2>;
    $[18] = mobileTitle;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== defaultExpanded) {
    t10 = defaultExpanded ? [ACCORDION_VALUE] : [];
    $[20] = defaultExpanded;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Image src={WandImage} alt="" width={20} height={20} className="size-20 shrink-0 object-contain" />;
    t12 = <span className="flex-1 font-600 text-Gray-800 typo-body4">내가 받을 수 있는 혜택</span>;
    $[22] = t11;
    $[23] = t12;
  } else {
    t11 = $[22];
    t12 = $[23];
  }
  let t13;
  if ($[24] !== totalRewardText) {
    t13 = totalRewardText ? <span className="font-600 text-Gray-black typo-body4">{totalRewardText}</span> : null;
    $[24] = totalRewardText;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13) {
    t14 = <Accordion.Trigger ellipsis={true} title={<span className="flex w-full items-center gap-4">{t11}{t12}{t13}</span>} hoverFeedback="icon" className="gap-4 p-16" />;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== entry || $[29] !== rewards) {
    t15 = <Accordion.Content className="px-16 pb-16 pt-0"><ul className="flex flex-col gap-16 rounded-small bg-Gray-25 p-12">{rewards.map(t16 => {
          const {
            eventName,
            rewardProductName: rewardProductName_0
          } = t16;
          return <li key={eventName} className="flex flex-col items-end gap-2"><span className="w-full truncate font-400 text-Gray-500 typo-caption1">{eventName}</span><RewardValue rewardProductName={rewardProductName_0} isNaverPay={entry === WEBVIEW_TYPE_ENTRY.KAKAO_BANK} /></li>;
        })}</ul></Accordion.Content>;
    $[28] = entry;
    $[29] = rewards;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t14 || $[32] !== t15) {
    t16 = <Accordion.Item value={ACCORDION_VALUE} className="rounded-medium border border-B-300 bg-Gray-white">{t14}{t15}</Accordion.Item>;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== className || $[35] !== t10 || $[36] !== t16) {
    t17 = <Accordion variant="separated" defaultValues={t10} className={className}>{t16}</Accordion>;
    $[34] = className;
    $[35] = t10;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t17 || $[39] !== t9) {
    t18 = <section className="flex flex-col gap-8">{t9}{t17}</section>;
    $[38] = t17;
    $[39] = t9;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  return t18;
};
const RewardValue = t0 => {
  const $ = _c(8);
  const {
    rewardProductName,
    isNaverPay
  } = t0;
  let t1;
  let t2;
  if ($[0] !== isNaverPay || $[1] !== rewardProductName) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const amount = parseRewardAmount(rewardProductName);
      if (amount === null) {
        let t3;
        if ($[4] !== rewardProductName) {
          t3 = rewardProductName?.trim() ? <span className="font-600 text-Gray-800 typo-caption1">{rewardProductName}</span> : null;
          $[4] = rewardProductName;
          $[5] = t3;
        } else {
          t3 = $[5];
        }
        t2 = t3;
        break bb0;
      }
      if (isNaverPay) {
        let t3;
        if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <span className="font-400">네이버페이</span>;
          $[6] = t3;
        } else {
          t3 = $[6];
        }
        t2 = <span className="flex items-center gap-4 whitespace-nowrap text-Gray-800 typo-caption1">{t3}<span className="font-600">{amount.toLocaleString("ko-KR")}원</span></span>;
        break bb0;
      }
      let t3;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = <SvgWelloPoint role="img" aria-label={"\uC6F0\uD3EC\uC778\uD2B8"} originalFill={true} className="size-15 shrink-0" />;
        $[7] = t3;
      } else {
        t3 = $[7];
      }
      t1 = <span className="flex items-center gap-4 whitespace-nowrap font-600 text-Gray-800 typo-caption1">{t3}{amount.toLocaleString("ko-KR")}원</span>;
    }
    $[0] = isNaverPay;
    $[1] = rewardProductName;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  return t1;
};
function _temp(state) {
  return state.isLogin;
}
function _temp2(t0) {
  const {
    context
  } = t0;
  return context;
}
function _temp3(state) {
  return state.myInfo?.memberCode;
}
function _temp4(onChange) {
  cookies.addChangeListener(onChange);
  return () => cookies.removeChangeListener(onChange);
}
function _temp5() {
  return cookies.get(COOKIES_KEYS.INVITE_MEMBER_CODE);
}
function _temp6() {}
function _temp7(t0) {
  const {
    context: context_0
  } = t0;
  return context_0?.inviteeUnitAmount;
}
function _temp8(t0) {
  const {
    rewardProductName
  } = t0;
  return rewardProductName;
}